import type { PresetRole } from '@projoint/shared';

import settings from '@/shared/i18n/en/settings-tabs';
import { labelMap, tr } from '@/shared/i18n/translate';
import { term } from '@/shared/lib/glossary';

/**
 * Team settings tabs — one table shared by the desktop rail
 * (`settings-page.tsx`) and the phone menu (`mobile/team-settings-mobile.tsx`),
 * so a role sees the same set on both (docs/release-1/17 §6 W5-D).
 *
 * The orphan admin routes fold in here (Appendix A §9 cut list):
 * `/integrations`, `/knowledge`, `/trust-center` and `/teams/:slug/webhooks`
 * redirect to `/settings?tab=…` (see `legacySettingsSearch`).
 */
export const SETTINGS_TAB_IDS = [
  'general',
  'billing',
  'members',
  'workflow',
  // Personal: my projects (archive / move / restore). Team: archived projects → Restore.
  'projects',
  // Personal only (§7 W6-D): Personal › Rituals and Personal › AI (my own key).
  'rituals',
  // Personal only: deadline / time nudges for my own work (off by default).
  'nudges',
  'ai',
  'policies',
  'roles',
  'integrations',
  'webhooks',
  'knowledge',
  'trust-center',
  // Personal only: calendar feed, API / MCP access — what one person connects.
  'connections',
  // Personal only: export my Personal tasks, storage used.
  'data',
  'danger',
] as const;

export type SettingsTabId = (typeof SETTINGS_TAB_IDS)[number];

const ALL: readonly PresetRole[] = ['OWNER', 'MANAGER', 'LEAD', 'MEMBER', 'OUTSTAFF', 'VIEWER'];
const MANAGERS: readonly PresetRole[] = ['OWNER', 'MANAGER'];
const LEADS: readonly PresetRole[] = ['OWNER', 'MANAGER', 'LEAD'];

/**
 * Which preset roles see each tab. Mirrors the backend gates (docs/22 §4):
 * `team.edit` (general, billing), `team.manage_workflow` / `workflow.admin`
 * (workflow, roles), `governance.policy_admin` (policies),
 * `team.manage_integrations` (integrations), `project.manage_workflow`
 * (webhook writes), `ai.agent_admin`-adjacent knowledge admin. The API still
 * rejects every write it doesn't allow — this only hides what can't be used.
 */
export const TAB_VISIBLE_TO: Readonly<Record<SettingsTabId, readonly PresetRole[]>> = {
  general: MANAGERS,
  billing: MANAGERS,
  members: ALL,
  workflow: LEADS,
  // Team: archived projects + Restore — `project.archive` (Owner / Manager). In Personal
  // I'm always the Owner.
  projects: MANAGERS,
  // Personal-only tabs (see PERSONAL_ONLY_TABS).
  rituals: ALL,
  nudges: ALL,
  ai: ALL,
  connections: ALL,
  data: ALL,
  policies: MANAGERS,
  roles: LEADS,
  integrations: MANAGERS,
  webhooks: LEADS,
  knowledge: LEADS,
  // Governance is transparent to the team (trust-center.service): every
  // regular member can read the timeline; Contractors and Guests can't.
  'trust-center': ['OWNER', 'MANAGER', 'LEAD', 'MEMBER'],
  // Everyone: owners get transfer/delete, everyone else "Leave team".
  danger: ALL,
};

/**
 * Tabs that exist only in my Personal settings: my projects, rituals (morning
 * plan, wrap-up, weekly review pushes), nudges, my own AI key (D7), what I
 * connect (calendar feed, API / MCP) and my data (export, storage). PRIVATE —
 * they are about me, never a team's settings (§2.2).
 */
export const PERSONAL_ONLY_TABS: ReadonlySet<SettingsTabId> = new Set<SettingsTabId>([
  'rituals',
  'nudges',
  'ai',
  'connections',
  'data',
]);

/** A Personal space is single-member: no collaboration or governance tabs. */
export const PERSONAL_HIDDEN_TABS: ReadonlySet<SettingsTabId> = new Set<SettingsTabId>([
  'members',
  'roles',
  'policies',
  'integrations',
  'webhooks',
  'knowledge',
  'trust-center',
  'danger',
]);

/** Tab labels in the current language (read on each access). */
export const SETTINGS_TAB_LABEL: Readonly<Record<SettingsTabId, string>> = labelMap(settings, {
  general: 'tabGeneral',
  billing: 'tabBilling',
  members: 'tabMembers',
  workflow: 'tabWorkflow',
  projects: 'tabProjects',
  rituals: 'tabRituals',
  nudges: 'tabNudges',
  ai: 'tabAi',
  policies: 'tabPolicies',
  roles: 'tabRoles',
  integrations: 'tabIntegrations',
  webhooks: 'tabWebhooks',
  knowledge: 'tabKnowledge',
  'trust-center': 'tabTrustCenter',
  connections: 'tabConnections',
  data: 'tabData',
  danger: 'tabDanger',
});

/**
 * One plain-language line per tab — under its name in the desktop rail and on
 * the phone menu row, so nobody has to open a tab to learn what it is for.
 * General / Billing / Workflow read differently in a Personal space.
 */
const TAB_HINT_KEY: Readonly<Record<SettingsTabId, keyof typeof settings.en & string>> = {
  general: 'hintGeneral',
  billing: 'hintBilling',
  members: 'hintMembers',
  workflow: 'hintWorkflow',
  projects: 'hintTeamProjects',
  rituals: 'hintRituals',
  nudges: 'hintNudges',
  ai: 'hintAi',
  policies: 'hintPolicies',
  roles: 'hintRoles',
  integrations: 'hintIntegrations',
  webhooks: 'hintWebhooks',
  knowledge: 'hintKnowledge',
  'trust-center': 'hintTrustCenter',
  connections: 'hintConnections',
  data: 'hintData',
  danger: 'hintDanger',
};
const PERSONAL_HINT_KEY: Readonly<Partial<Record<SettingsTabId, keyof typeof settings.en & string>>> = {
  general: 'hintPersonalGeneral',
  billing: 'hintPersonalBilling',
  workflow: 'hintPersonalWorkflow',
  projects: 'hintProjects',
};

/** The tab's one-line description in the current language. */
export function settingsTabHint(id: SettingsTabId, personal: boolean): string {
  const key = (personal ? PERSONAL_HINT_KEY[id] : undefined) ?? TAB_HINT_KEY[id];
  return tr(settings, key);
}

export type SettingsGroupId =
  | 'groupTeam'
  | 'groupSpace'
  | 'groupPeople'
  | 'groupWork'
  | 'groupMyDay'
  | 'groupGovernance'
  | 'groupConnections'
  | 'groupData'
  | 'groupDanger';

interface SettingsGroup {
  id: SettingsGroupId;
  tabs: readonly SettingsTabId[];
}

const TEAM_GROUPS: readonly SettingsGroup[] = [
  { id: 'groupTeam', tabs: ['general', 'billing'] },
  { id: 'groupPeople', tabs: ['members', 'roles'] },
  { id: 'groupWork', tabs: ['workflow', 'projects'] },
  { id: 'groupGovernance', tabs: ['policies', 'trust-center'] },
  { id: 'groupConnections', tabs: ['integrations', 'webhooks', 'knowledge'] },
  { id: 'groupDanger', tabs: ['danger'] },
];
const PERSONAL_GROUPS: readonly SettingsGroup[] = [
  { id: 'groupSpace', tabs: ['general', 'billing'] },
  { id: 'groupWork', tabs: ['workflow', 'projects'] },
  { id: 'groupMyDay', tabs: ['rituals', 'nudges', 'ai'] },
  { id: 'groupConnections', tabs: ['connections'] },
  { id: 'groupData', tabs: ['data'] },
];

/**
 * The rail / phone menu as labelled groups (Team · People · Work · …, or for
 * Personal: Space · Work · My day · Connections · Data), each with only the
 * tabs this viewer gets. Empty groups are dropped. Shared by desktop and phone
 * so both show the same structure.
 */
/** A group heading in the current language. */
export function settingsGroupLabel(id: SettingsGroupId): string {
  return tr(settings, id);
}

export function settingsGroups(team: SettingsTeamLike | null | undefined): Array<{ id: SettingsGroupId; label: string; tabs: SettingsTabId[] }> {
  const visible = visibleSettingsTabs(team);
  if (visible.length === 0) return [];
  const groups = team?.kind === 'personal' ? PERSONAL_GROUPS : TEAM_GROUPS;
  return groups
    .map((g) => ({ id: g.id, label: settingsGroupLabel(g.id), tabs: g.tabs.filter((id) => visible.includes(id)) }))
    .filter((g) => g.tabs.length > 0);
}

/** Tabs drawn as a child of another tab in the desktop rail (Integrations › Webhooks). */
export const NESTED_TAB_PARENT: Readonly<Partial<Record<SettingsTabId, SettingsTabId>>> = {
  webhooks: 'integrations',
};
export const NESTED_TABS: ReadonlySet<SettingsTabId> = new Set<SettingsTabId>(
  Object.keys(NESTED_TAB_PARENT) as SettingsTabId[],
);

/**
 * Indent `id` under its parent only when that parent is in this viewer's rail
 * too. A Lead sees Webhooks but not Integrations (Owner / Manager only) — then
 * Webhooks is a plain top-level tab, never an orphaned child.
 */
export function isNestedInRail(id: SettingsTabId, visible: readonly SettingsTabId[]): boolean {
  const parent = NESTED_TAB_PARENT[id];
  return !!parent && visible.includes(parent);
}

export interface SettingsTeamLike {
  kind: 'personal' | 'regular';
  myRolePreset: PresetRole | null;
}

/** The tabs this viewer gets for `team`, in rail order (none until the team is known). */
export function visibleSettingsTabs(team: SettingsTeamLike | null | undefined): SettingsTabId[] {
  if (!team) return [];
  const role: PresetRole = team.myRolePreset ?? 'VIEWER';
  const personal = team.kind === 'personal';
  return SETTINGS_TAB_IDS.filter(
    (id) =>
      TAB_VISIBLE_TO[id].includes(role) &&
      !(personal && PERSONAL_HIDDEN_TABS.has(id)) &&
      !(!personal && PERSONAL_ONLY_TABS.has(id)),
  );
}

export function isSettingsTab(value: unknown): value is SettingsTabId {
  return typeof value === 'string' && (SETTINGS_TAB_IDS as readonly string[]).includes(value);
}

/**
 * `/settings` search for an old standalone admin route that now lives in a
 * Settings tab. Keeps `?team=` when the old link carried it, so a redirect
 * never silently switches team.
 */
export function legacySettingsSearch(
  tab: SettingsTabId,
  search: { team?: unknown } | undefined,
): { tab: SettingsTabId; team?: string } {
  const team = typeof search?.team === 'string' && search.team ? search.team : undefined;
  return team ? { tab, team } : { tab };
}

/** Validates `?team=` on the redirect-only legacy routes. */
export function validateLegacyTeamSearch(raw: Record<string, unknown>): { team?: string } {
  return typeof raw.team === 'string' && raw.team ? { team: raw.team } : {};
}

/** Settings page title: "Acme · Settings", or "Team settings" before a team is picked. */
export function settingsTitle(teamName: string | null | undefined): string {
  return teamName ? tr(settings, 'pageTitle', { team: teamName }) : term('teamSettings');
}
